import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Label,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@tempmee/react';
import { TableCard } from '@tempmee/react-blocks/experimental';
import { OutlineCheckCircleIcon } from '@tempmee/react-icons';
import { getMembershipStatusSeverity } from '../../../../domain/Awards';

// The last column is named for what it is rather than for the one control it
// holds today, so adding a second row action later needs no rename.
const COLUMNS = ['Name', 'User Type', 'Status', 'Application', 'Feedback', 'Actions'];

// hasApplication / hasFeedback are real booleans now — awards-service GOTU-5889
// wired them (GOTU-6449) after this screen was first built against the
// hardcoded-false contract. Presence is shown as a check with no link to
// follow: there is no admin endpoint to retrieve the application or feedback
// itself yet (GOTU-5632 unbuilt), so a link would only dead-end. Absence is a
// muted en dash — at body colour a column of dashes reads as content rather
// than absence.
const NotAvailable = () => <span className="text-text-secondary">–</span>;

const PresenceCell = ({ present, label }) =>
  (present ? (
    // The DS icon defaults to aria-hidden; here it is the cell's only content,
    // so it must carry the accessible name instead of being hidden.
    <OutlineCheckCircleIcon
      role="img"
      aria-hidden={false}
      aria-label={label}
      className="size-5 text-success-500"
    />
  ) : (
    <NotAvailable />
  ));

PresenceCell.propTypes = {
  present: PropTypes.bool.isRequired,
  /** Accessible name for the check; the icon is the only content of the cell. */
  label: PropTypes.string.isRequired,
};

/**
 * The cycle roster. The filters ride in TableCard's toolbar slot.
 *
 * The error branch is ours: TableCard has no failure state, and "no users yet"
 * must not stand in for a request that failed.
 */
export const MembershipTable = ({
  memberships,
  isPending,
  isError,
  isEditable,
  renderStatus,
  toolbar,
  footer,
  emptyMessage,
}) => {
  if (isError) {
    return (
      <TableCard title="Users" toolbar={toolbar}>
        <Typography className="text-error">Could not load users.</Typography>
      </TableCard>
    );
  }

  return (
    <TableCard
      title="Users"
      toolbar={toolbar}
      footer={footer}
      loading={isPending}
      empty={!isPending && memberships.length === 0}
      emptyMessage={emptyMessage}
    >
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
          {memberships.map((row) => (
            <TableRow key={row.userId} hover>
              <TableCell>{row.displayName}</TableCell>
              <TableCell>{row.userType}</TableCell>
              <TableCell>
                {isEditable ? (
                  renderStatus(row)
                ) : (
                  <Label severity={getMembershipStatusSeverity(row.status)} variant="standard">
                    {row.statusLabel}
                  </Label>
                )}
              </TableCell>
              <TableCell>
                <PresenceCell present={row.hasApplication} label="Application submitted" />
              </TableCell>
              <TableCell>
                <PresenceCell present={row.hasFeedback} label="Feedback submitted" />
              </TableCell>
              <TableCell>
                {/* ADM rows have no domain profile page. */}
                {row.profilePath ? (
                  <RouterLink to={row.profilePath}>View Profile</RouterLink>
                ) : (
                  <NotAvailable />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
};

MembershipTable.propTypes = {
  memberships: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      userType: PropTypes.string.isRequired,
      statusLabel: PropTypes.string.isRequired,
      hasApplication: PropTypes.bool.isRequired,
      hasFeedback: PropTypes.bool.isRequired,
      profilePath: PropTypes.string,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  isEditable: PropTypes.bool.isRequired,
  /** Renders the editable status control for a row. Ignored when not editable. */
  renderStatus: PropTypes.func.isRequired,
  /** Filter bar, rendered in the card's toolbar slot. */
  toolbar: PropTypes.node,
  /** Footer slot — the page passes its pagination. */
  footer: PropTypes.node,
  /**
   * Shown when the roster is empty. The page varies it so a filtered-to-zero
   * result reads differently from a genuinely empty cycle (GOTU-6400).
   */
  emptyMessage: PropTypes.string,
};

MembershipTable.defaultProps = {
  toolbar: null,
  footer: null,
  emptyMessage: 'No users in this cycle yet.',
};
